// src/features/delegations/hooks/useDelegationFiltering.js
//
// Admin hook — fetches ALL delegations in the department with full filter support.
// Used by users who have delegation:create.
import { useState, useEffect, useCallback } from 'react';
import { delegationsAPI } from '../services/delegations';
import { useApi } from '../../../hooks/api/useApi';

const DEBOUNCE_MS = 400;

export function useDelegationFiltering(initialState = {}) {
  const { loading, error, execute, clearError } = useApi();

  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm || '');
  const [debouncedSearch, setDebouncedSearch] = useState(initialState.searchTerm || '');
  const [statuses, setStatuses] = useState(initialState.statuses || []);

  const [delegations, setDelegations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadDelegations = useCallback(async () => {
    const body = {};
    if (debouncedSearch.trim()) body.search_term = debouncedSearch.trim();
    if (statuses.length > 0) body.statuses = statuses;

    const result = await execute(() => delegationsAPI.filterDelegations(body));
    if (result.success) {
      const data = result.data?.data;
      setDelegations(data?.delegations || []);
      setTotalCount(data?.total_count ?? 0);
    }
  }, [debouncedSearch, statuses, execute]);

  useEffect(() => {
    loadDelegations();
  }, [loadDelegations]);

  const hasActiveFilters = searchTerm !== '' || statuses.length > 0;

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setStatuses([]);
  }, []);

  return {
    delegations,
    totalCount,
    loading,
    error,
    clearError,
    searchTerm,
    statuses,
    hasActiveFilters,
    setSearchTerm,
    setStatuses,
    clearFilters,
    refreshData: loadDelegations,
  };
}
